import { Fragment, useEffect, useRef, useState, type ReactNode } from "react";
import { Check, Plus, X } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/i18n/LanguageContext";
import {
  PRICING_PLANS,
  PRICING_SECTIONS,
  PRICING_HOME_FOOTER,
  getPricingCardCopy,
  type MatrixCell,
  type PricingCardContext,
} from "@/lib/pricingMatrix";
import { cn } from "@/lib/utils";
import AuthModal from "@/components/LazyAuthModal";
import StripeRedirectModal from "@/components/StripeRedirectModal";
import { useAuthState } from "@/hooks/useAuthState";
import { betaGateCheckout } from "@/lib/betaGate";
import { invokeCheckout } from "@/lib/checkoutRouter";
import { trackBeginCheckout } from "@/lib/analytics";
import { getSupabase } from "@/lib/supabase-lazy";
import { useLiteEnabled } from "@/hooks/useLiteEnabled";

function useVisiblePlans() {
  const liteOn = useLiteEnabled();
  return liteOn ? [...PRICING_PLANS] : PRICING_PLANS.filter((p) => p.key !== "lite");
}
const cellIdx = (key: string) => PRICING_PLANS.findIndex((p) => p.key === key);

type Lang = "es" | "en";
type PlanKey = (typeof PRICING_PLANS)[number]["key"];
const PLAN_PRICE_CENTS: Record<PlanKey, number> = { lite: 0, basico: 3900, club_pro: 6900, completo: 9900 };

const L = (lang: Lang, v: { es: string; en: string }) => (lang === "es" ? v.es : v.en);

/** Estilo de cada tarjeta de cabecera: Lite/Básico blancas, Club Pro amarillo, Avanzado negro. */
const CARD_STYLES: Record<string, string> = {
  lite: "bg-card text-foreground shadow-md border border-border/60",
  basico: "bg-card text-foreground shadow-xl border border-border/70",
  club_pro: "bg-primary text-primary-foreground shadow-2xl border border-primary -translate-y-2 scale-[1.025] z-10",
  completo: "bg-foreground text-background shadow-xl border border-foreground",
};

const SUBTLE: Record<string, string> = {
  lite: "text-muted-foreground",
  basico: "text-muted-foreground",
  club_pro: "text-primary-foreground/75",
  completo: "text-background/70",
};

function CellValue({ cell, lang }: { cell: MatrixCell; lang: Lang }) {
  switch (cell.kind) {
    case "yes":
      return (
        <Check className="h-5 w-5 text-success-strong mx-auto" strokeWidth={2.5} aria-label={lang === "es" ? "Incluido" : "Included"} />
      );
    case "no":
      return <X className="h-4.5 w-4.5 text-destructive/80 mx-auto" strokeWidth={2.5} aria-label={lang === "es" ? "No incluido" : "Not included"} />;
    case "limit":
      return <span className="font-figures text-lg font-normal tabular-nums">{cell.value}</span>;
    case "limited":
      return (
        <span className="text-xs font-semibold text-foreground/80">
          {lang === "es" ? "Limitada" : "Limited"}
        </span>
      );
    case "unlimited":
      return <span className="text-xs font-semibold text-foreground/80">{lang === "es" ? "Ilimitado" : "Unlimited"}</span>;
    case "extra":
      return (
        <span className="inline-flex items-center rounded-full border border-primary/50 bg-primary/15 px-3 py-1 text-[11px] font-semibold text-foreground">
          <Plus className="mr-1 h-3 w-3" aria-hidden="true" />Extra
        </span>
      );
  }
}

function PlanCard({ plan, lang, compact = false, context = "comparison", onSelect, loading = false, children }: { plan: (typeof PRICING_PLANS)[number]; lang: Lang; compact?: boolean; context?: PricingCardContext; onSelect: (key: PlanKey) => void; loading?: boolean; children?: ReactNode }) {
  const cardCopy = getPricingCardCopy(plan.key, context, lang);
  return (
    <div className={cn(
      "relative flex h-full flex-col items-center justify-center gap-1 rounded-lg px-3 text-center",
      compact ? "min-h-36 py-4" : "min-h-52 py-5",
      CARD_STYLES[plan.key],
      compact && plan.key === "club_pro" && "translate-y-0 scale-100",
      context === "home" && "grid grid-rows-[32px_20px_32px_52px_1fr] items-start justify-stretch min-w-0 translate-y-0 scale-100",
    )}>
      {"featured" in plan && plan.featured && (
        <span className="absolute -top-2 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-foreground px-2.5 py-1 text-[10px] font-semibold text-background shadow-md">
          {lang === "es" ? "El más popular" : "Most popular"}
        </span>
      )}
      <h3 className={cn("font-display font-extrabold drop-shadow-sm", compact ? "text-xl" : "text-2xl")}>{lang === "en" && "nameEn" in plan ? plan.nameEn : plan.name}</h3>
      {!compact && <p className={cn("min-h-8 text-xs leading-tight", context === "home" && "min-h-0 whitespace-nowrap", SUBTLE[plan.key])}>{L(lang, plan.tagline)}</p>}
      <p className="mt-1 flex items-baseline justify-center gap-1">
        <span className={cn("font-figures font-bold tabular-nums", compact ? "text-xl" : "text-2xl")}>{L(lang, plan.price)}</span>
        {L(lang, plan.period) && <span className={cn("text-xs", SUBTLE[plan.key])}>{L(lang, plan.period)}</span>}
      </p>
      <div className="mt-2 flex flex-col items-center">
        <Button type="button" size="sm" disabled={loading} onClick={() => onSelect(plan.key)} className={cn(
          "h-auto min-h-10 whitespace-normal rounded-full px-3 py-2 text-xs leading-tight",
          plan.key === "club_pro"
            ? "bg-foreground text-background hover:bg-foreground/90"
            : plan.key === "completo"
              ? "bg-primary text-primary-foreground hover:bg-primary/90"
              : "bg-foreground text-background hover:bg-foreground/90",
        )}>
          {loading ? (lang === "es" ? "Preparando…" : "Preparing…") : cardCopy.cta}
        </Button>
        {cardCopy.note && (
          <p className={cn("mt-2 text-[10px] leading-tight text-muted-foreground", plan.key === "completo" && "text-background/70")}>
            {cardCopy.note}
          </p>
        )}
      </div>
      {children && (
        <div className="mt-3 flex w-full min-h-0 flex-1 flex-col items-center self-stretch">
          {children}
        </div>
      )}
    </div>
  );
}

function MobilePlanComparison({ lang, onSelect, loadingKey }: { lang: Lang; onSelect: (key: PlanKey) => void; loadingKey: PlanKey | null }) {
  // Solo se comparan planes correlativos: Lite–Básico, Básico–Club Pro, Club Pro–Avanzado.
  const plans = useVisiblePlans();
  const [pair, setPair] = useState<PlanKey>("basico");
  const maxLeft = plans.length - 2;
  const leftIdx = Math.max(0, Math.min(maxLeft, plans.findIndex((p) => p.key === pair)));
  const setLeftIdx = (i: number) => setPair(plans[i]!.key);
  const leftPlan = plans[leftIdx]!;
  const rightPlan = plans[leftIdx + 1]!;
  const leftIndex = cellIdx(leftPlan.key);
  const rightIndex = cellIdx(rightPlan.key);
  const recentKeys: PlanKey[] = [leftPlan.key, rightPlan.key];

  const selectPlan = (key: PlanKey) => {
    const idx = plans.findIndex((plan) => plan.key === key);
    // Pulsar el plan de la izquierda baja un escalón y el de la derecha sube uno;
    // un plan no seleccionado forma pareja con su vecino más cercano al par actual.
    const next =
      idx < leftIdx ? idx
      : idx === leftIdx ? idx - 1
      : idx === leftIdx + 1 ? idx
      : idx - 1;
    setLeftIdx(Math.max(0, Math.min(maxLeft, next)));
  };

  return (
    <div className="lg:hidden">
      <div className="sticky top-16 z-20 -mx-1 border-b border-border bg-background/95 px-1 pb-3 pt-2 backdrop-blur-sm">
        <p className="mb-2 text-center text-[11px] font-semibold uppercase text-muted-foreground">
          {lang === "es" ? "Elige dos planes para comparar" : "Choose two plans to compare"}
        </p>
        <div className="mb-3 flex items-stretch gap-1.5" role="group" aria-label={lang === "es" ? "Planes para comparar" : "Plans to compare"}>
          {plans.map((plan) => {
            const isSelected = recentKeys.includes(plan.key);
            return (
              <Button
                key={plan.key}
                type="button"
                variant="outline"
                aria-pressed={isSelected}
                onClick={() => selectPlan(plan.key)}
                className={cn(
                  "h-auto min-h-12 min-w-0 rounded-full px-1.5 py-2 transition-[flex,box-shadow,transform] motion-reduce:transition-none",
                  "flex flex-col gap-0.5 whitespace-normal leading-none",
                  isSelected ? "flex-[1.25] -translate-y-0.5 shadow-md ring-2 ring-foreground/15" : "flex-[0.75] opacity-70",
                  plan.key === "club_pro" && "border-primary bg-primary text-primary-foreground hover:bg-primary/90",
                  plan.key === "completo" && "border-foreground bg-foreground text-background hover:bg-foreground/90",
                  (plan.key === "lite" || plan.key === "basico") && "bg-card text-foreground hover:bg-muted",
                )}
              >
                <span className={cn("font-display font-bold", isSelected ? "text-[13px]" : "text-[11px]")}>{lang === "en" && "nameEn" in plan ? plan.nameEn : plan.name}</span>
                <span className={cn("font-figures tabular-nums", isSelected ? "text-[11px]" : "text-[10px]")}>{L(lang, plan.price)}{L(lang, plan.period)}</span>
              </Button>
            );
          })}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <PlanCard plan={leftPlan} lang={lang} compact onSelect={onSelect} loading={loadingKey === leftPlan.key} />
          <PlanCard plan={rightPlan} lang={lang} compact onSelect={onSelect} loading={loadingKey === rightPlan.key} />
        </div>
      </div>

      <Accordion type="multiple" defaultValue={["section-0"]} className="mt-3 overflow-hidden rounded-lg border border-border bg-card">
        {PRICING_SECTIONS.map((section, sectionIndex) => (
          <AccordionItem key={section.title.es} value={`section-${sectionIndex}`} className="border-border last:border-b-0">
            <AccordionTrigger className="min-h-12 bg-muted/50 px-4 py-3 text-left hover:no-underline">
              <span className="min-w-0 font-display text-sm font-extrabold uppercase">
                {L(lang, section.title)}
                {section.tag && (
                  <span className="ml-2 inline-flex rounded-full bg-foreground px-2 py-0.5 align-middle font-body text-[10px] font-semibold normal-case text-background">
                    {L(lang, section.tag)}
                  </span>
                )}
              </span>
            </AccordionTrigger>
            <AccordionContent className="pb-0">
              {section.rows.map((row) => (
                <div key={row.label.es} className="border-t border-border/60 px-4 py-3 first:border-t-0">
                  <div className="mb-2.5 min-w-0">
                    <p className="font-medium leading-snug">{L(lang, row.label)}</p>
                    {row.sublabel && <p className="mt-0.5 text-xs leading-snug text-muted-foreground">{L(lang, row.sublabel)}</p>}
                  </div>
                  <div className="grid grid-cols-2 gap-2 rounded-md bg-muted/35 py-2">
                    <div className="min-w-0 text-center"><CellValue cell={row.cells[leftIndex] ?? row.cells[0]} lang={lang} /></div>
                    <div className="min-w-0 border-l border-border text-center"><CellValue cell={row.cells[rightIndex] ?? row.cells[1]} lang={lang} /></div>
                  </div>
                </div>
              ))}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

const PricingMatrixTable = ({ compact = false }: { compact?: boolean }) => {
  const { lang: ctxLang } = useLanguage();
  const lang: Lang = ctxLang === "en" ? "en" : "es";
  const action = usePricingPlanAction(lang);
  const plans = useVisiblePlans();
  const idxs = plans.map((p) => cellIdx(p.key));

  return (
    <div className="w-full">
      <MobilePlanComparison lang={lang} onSelect={action.selectPlan} loadingKey={action.loadingKey} />

      {/* Una sola cuadrícula mantiene cada cabecera alineada con sus prestaciones. */}
      <div className="mx-auto hidden max-w-6xl overflow-x-auto rounded-lg border border-border/60 bg-card shadow-xl lg:block">
        <table className="w-full min-w-[790px] lg:min-w-[900px] table-fixed border-collapse text-sm">
          <colgroup>
            <col className="w-[190px] lg:w-[32%]" />
            {plans.map((plan) => <col key={plan.key} className="w-[150px] lg:w-[17%]" />)}
          </colgroup>
          <thead>
            <tr className="align-stretch">
              <th className="px-5 pb-5 pt-7 text-left align-bottom font-body text-xs uppercase tracking-wider text-muted-foreground">
                {lang === "es" ? "Qué incluye cada plan" : "What each plan includes"}
              </th>
              {plans.map((plan) => (
                <th key={plan.key} className="p-2 pt-7 align-stretch text-center">
                  <PlanCard plan={plan} lang={lang} onSelect={action.selectPlan} loading={action.loadingKey === plan.key} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PRICING_SECTIONS.map((section) => (
              <Fragment key={section.title.es}>
                <tr className="bg-muted/50">
                  <td colSpan={plans.length + 1} className="px-4 sm:px-6 py-2.5">
                    <span className="font-display text-xs sm:text-sm font-extrabold uppercase tracking-wide">
                      {L(lang, section.title)}
                    </span>
                    {section.tag && (
                      <span className="ml-2 rounded-full bg-foreground text-background text-[10px] font-semibold px-2 py-0.5 align-middle">
                        {L(lang, section.tag)}
                      </span>
                    )}
                  </td>
                </tr>
                {section.rows.map((row) => (
                  <tr key={row.label.es} className="border-t border-border/40 hover:bg-muted/30 transition-colors">
                    <td className="px-4 sm:px-6 py-3">
                      <span className="font-medium">{L(lang, row.label)}</span>
                      {row.sublabel && (
                        <span className="block text-xs text-muted-foreground">{L(lang, row.sublabel)}</span>
                      )}
                    </td>
                    {idxs.map((i) => row.cells[i]!).map((cell, j) => (
                      <td
                        key={j}
                        className={cn(
                          "px-2 sm:px-4 py-3 text-center",
                          plans[j]?.key === "completo" && "bg-foreground/4"
                        )}
                      >
                        <CellValue cell={cell} lang={lang} />
                      </td>
                    ))}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 max-w-6xl mx-auto">
        <p className="text-center text-sm font-semibold text-foreground">{L(lang, PRICING_HOME_FOOTER)}</p>
      </div>
      {action.dialogs}
    </div>
  );
};

/** Solo las 4 tarjetas (home): ventajas clave desde la matriz + enlace al detalle. */
export const PricingCards = ({ carousel = false, centerKey, onSelectOverride }: { carousel?: boolean; centerKey?: PlanKey; onSelectOverride?: (key: PlanKey) => void } = {}) => {
  const { lang: ctxLang } = useLanguage();
  const lang: Lang = ctxLang === "en" ? "en" : "es";
  const to = lang === "es" ? "/tarifas" : "/en/pricing";
  const realAction = usePricingPlanAction(lang);
  // Vista previa del administrador: los botones no inician ningún cobro.
  const action = onSelectOverride ? { ...realAction, selectPlan: async (k: PlanKey) => onSelectOverride(k), loadingKey: null, dialogs: null } : realAction;
  const plans = useVisiblePlans();
  const railRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!carousel || !centerKey) return;
    const id = window.setTimeout(() => {
      const rail = railRef.current;
      const el = rail?.querySelector<HTMLElement>(`[data-plan="${centerKey}"]`);
      if (rail && el) rail.scrollLeft = el.offsetLeft - (rail.clientWidth - el.clientWidth) / 2;
    }, 60);
    return () => window.clearTimeout(id);
  }, [carousel, centerKey]);
  return (
    <div className="w-full">
      <div
        ref={railRef}
        className={cn(
          carousel
            ? "-mx-6 flex snap-x snap-mandatory items-stretch gap-4 overflow-x-auto px-[9vw] pb-2 pt-3 [scrollbar-width:none] sm:mx-auto sm:grid sm:max-w-6xl sm:grid-cols-3 sm:overflow-visible sm:px-0 [&>*]:w-[80vw] [&>*]:shrink-0 [&>*]:snap-center sm:[&>*]:w-auto"
            : cn("mx-auto grid max-w-6xl grid-cols-1 items-stretch gap-5 pt-3 sm:grid-cols-2", plans.length === 4 ? "lg:grid-cols-[0.85fr_1fr_1.15fr_1.3fr]" : "lg:grid-cols-[1fr_1.15fr_1.3fr]"),
        )}
      >
        {plans.map((plan) => (
          <div key={plan.key} data-plan={plan.key} className="flex [&>*]:w-full">
          <PlanCard plan={plan} lang={lang} context="home" onSelect={action.selectPlan} loading={action.loadingKey === plan.key}>
            <ul className="w-full space-y-1.5 text-left">
              {plan.highlights.filter((h) => plans.length === 4 || h.es !== "Todo lo de Lite").map((h) => (
                <li key={h.es} className="flex items-start gap-2 text-xs leading-snug">
                  {"extra" in h && h.extra ? (
                    <Plus className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={2} aria-hidden="true" />
                  ) : (
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success-strong" strokeWidth={2.5} aria-hidden="true" />
                  )}
                  <span>{L(lang, h)}</span>
                </li>
              ))}
            </ul>
            <Link to={to} className="mt-auto pt-3 text-xs underline-offset-4 hover:underline">
              {lang === "es" ? "Ver detalle →" : "See details →"}
            </Link>
          </PlanCard>
          </div>
        ))}
      </div>
      <p className="mt-8 text-center text-sm text-muted-foreground">{L(lang, PRICING_HOME_FOOTER)}</p>
      {action.dialogs}
    </div>
  );
};

function usePricingPlanAction(lang: Lang) {
  const { isAuthenticated, isReady } = useAuthState();
  const [authOpen, setAuthOpen] = useState(false);
  const [loadingKey, setLoadingKey] = useState<PlanKey | null>(null);
  const [stripeUrl, setStripeUrl] = useState<string | null>(null);

  const selectPlan = async (key: PlanKey) => {
    if (loadingKey) return;
    const pending = key === "lite"
      ? { product_type: "lite", product_key: "lite", billing_interval: "month", is_paid_intent: false }
      : { product_type: "plan", product_key: key, billing_interval: "month", is_paid_intent: true };
    localStorage.setItem("pending_checkout", JSON.stringify(pending));
    setLoadingKey(key);
    try {
      let signedIn = isAuthenticated;
      if (!isReady) {
        const supabase = await getSupabase();
        const { data } = await supabase.auth.getSession();
        signedIn = Boolean(data.session);
      }
      if (!signedIn) {
        setAuthOpen(true);
        return;
      }
      const blocked = await betaGateCheckout({ plan: key, interval: "month", lang });
      if (blocked) {
        localStorage.removeItem("pending_checkout");
        return;
      }
      if (key === "lite") {
        localStorage.removeItem("pending_checkout");
        window.location.assign("/dashboard");
        return;
      }
      const { data, error } = await invokeCheckout({ body: pending });
      if (error || !data?.url) throw error ?? new Error("checkout_unavailable");
      trackBeginCheckout({ source: "tarifas", productType: "plan", productKey: key, interval: "month", valueCents: PLAN_PRICE_CENTS[key] });
      localStorage.removeItem("pending_checkout");
      setStripeUrl(data.url);
    } catch {
      const { toast } = await import("sonner");
      toast.error(lang === "es" ? "No pudimos iniciar el alta. Inténtalo de nuevo." : "We couldn't start signup. Please try again.");
    } finally {
      setLoadingKey(null);
    }
  };

  return {
    selectPlan,
    loadingKey,
    dialogs: (
      <>
        <AuthModal open={authOpen} onOpenChange={(open) => { setAuthOpen(open); if (!open) setLoadingKey(null); }} initialMode="signup" />
        <StripeRedirectModal open={Boolean(stripeUrl)} stripeUrl={stripeUrl} />
      </>
    ),
  };
}

export default PricingMatrixTable;
